import React from 'react';
import PropTypes from 'prop-types';
import { Button, Plus } from '@opendate-app/setlist';

import ArtistPickerField from '../../shared/components/ArtistPickerField';
import { fetchArtistOptions } from '../../shared/components/ArtistSelectComponents';

// Empty-state artist slot in the OfferEditUI header (O-4907): the event's
// still-unattached lineup performers render as a one-click "From this event"
// strip, with the universal picker below for searching the whole roster (or
// creating via Spotify). Both paths build the same offer update the old
// grouped AsyncSelect produced — a lineup pick writes `performer` (nested
// performer row), a search pick writes `performer` when the offer has a
// calendar_event and `primary_performer` otherwise — and hand it to onUpdate,
// which owns updateOffer + autosave.
const OfferHeaderArtistPicker = ({ offer, teamId, onUpdate }) => {
  const lineupPerformers = (offer.calendar_event?.performers || []).filter(p => !p.offer?.id);

  // An artist already on the event lineup is attached via the strip above (which
  // writes the existing performer_id), never re-picked from search. A search
  // pick writes artistable_id, so picking a lineup artist would mint a second
  // Performer for them — the old grouped select filtered them out to prevent
  // this. We instead render them disabled with a badge: it blocks the duplicate
  // pick AND keeps isExactMatch true so the create-new-artist row stays hidden
  // on an exact name match (filtering them out would resurface it).
  const lineupArtistIds = new Set(
    (offer.calendar_event?.performers || []).map(p => p.artistable?.id).filter(Boolean),
  );
  const fetchOptions = (query, callback) => {
    fetchArtistOptions(query, (opts) => {
      callback((opts || []).map(o => (
        lineupArtistIds.has(o.value) ? { ...o, disabled: true, badge: 'On this lineup' } : o
      )));
    });
  };

  const pickLineupPerformer = (performer) => {
    onUpdate({
      ...offer,
      savePerformer: true,
      performer: {
        artistable: performer,
        id: performer.id,
        option: {
          value: performer.id,
          label: `${performer.artistable.name} - ${performer.humanized_act_type}`,
        },
      },
    });
  };

  const pickArtist = ({ artist }) => {
    // The header card renders image_thumb_url_for_json until the save
    // round-trip refreshes the offer, so alias the picker's image_thumb_url
    // onto it for the interim render.
    const artistable = { ...artist, image_thumb_url_for_json: artist.image_thumb_url };
    const option = { value: artist.id, label: artist.name };

    if (offer.calendar_event) {
      onUpdate({
        ...offer,
        savePerformer: true,
        performer: { artistable, id: artist.id, option },
      });
    } else {
      onUpdate({
        ...offer,
        savePerformer: true,
        primary_performer: { artistable, option },
      });
    }
  };

  return (
    <>
      {lineupPerformers.length > 0 && (
        <div className="mb-2" data-testid="offer-lineup-strip">
          <p className="offer-lineup-strip__label mb-1">FROM THIS EVENT</p>
          <div className="offer-lineup-strip__chips">
            {lineupPerformers.map(performer => (
              <Button key={performer.id}
                      variant="secondary-color"
                      size="sm"
                      icon={Plus}
                      data-testid="offer-lineup-strip-performer"
                      onClick={() => pickLineupPerformer(performer)}>
                {performer.artistable.name} · {performer.humanized_act_type}
              </Button>
            ))}
          </div>
        </div>
      )}
      <div className="form-group mb-0">
        <ArtistPickerField
          id="offer-header-artist"
          placeholder="Add Artist"
          teamId={teamId}
          fetchOptions={fetchOptions}
          onSelect={pickArtist}
        />
      </div>
    </>
  );
};

OfferHeaderArtistPicker.propTypes = {
  offer: PropTypes.object.isRequired,
  teamId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  onUpdate: PropTypes.func.isRequired,
};

export default OfferHeaderArtistPicker;
